import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Upload, Download, FileText, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientImport = () => {
  const { token } = useAuth();
  const [importFile, setImportFile] = useState(null);
  const [updateFile, setUpdateFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);
  const [activeTab, setActiveTab] = useState('import'); // 'import' or 'update'

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients/template`, {
        ...axiosConfig,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_importacion_clientes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al descargar la plantilla');
    }
  };

  // NEW: Download existing clients for update
  const handleDownloadClientsForUpdate = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients/export`, {
        ...axiosConfig,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clientes_para_actualizar.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al descargar los clientes');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  // NEW: Handle file change for update
  const handleUpdateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
        return;
      }
      setUpdateFile(file);
      setUpdateResult(null);
    }
  };

  // NEW: Handle bulk update
  const handleBulkUpdate = async () => {
    if (!updateFile) {
      alert('Por favor selecciona un archivo');
      return;
    }

    setUpdating(true);
    setUpdateResult(null);

    try {
      const formData = new FormData();
      formData.append('file', updateFile);

      const response = await axios.post(`${API}/admin/clients/bulk-update`, formData, {
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUpdateResult(response.data);
      setUpdateFile(null);
      // Reset file input
      const fileInput = document.getElementById('update-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      setUpdateResult({
        success: false,
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Por favor selecciona un archivo');
      return;
    }

    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/admin/clients/import`, formData, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      setImportFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      setImportResult({
        success: false,
        error: error.response?.data?.detail || error.message,
        imported_count: 0,
        error_count: 0
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('import')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'import'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📥 Importar Nuevos Clientes
        </button>
        <button
          onClick={() => setActiveTab('update')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'update'
              ? 'border-b-2 border-green-500 text-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🔄 Actualizar Clientes Existentes
        </button>
      </div>

      {/* IMPORT TAB */}
      {activeTab === 'import' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Importación Masiva de Clientes</CardTitle>
            <CardDescription>
              Importa múltiples clientes nuevos usando un archivo Excel
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Download Template */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold">Descarga la Plantilla</h3>
              </div>
              <p className="text-sm text-gray-600 ml-10">
                Descarga la plantilla de Excel con el formato correcto y los campos requeridos.
              </p>
              <div className="ml-10">
                <Button onClick={handleDownloadTemplate} variant="outline" className="gap-2">
                  <Download size={16} />
                  Descargar Plantilla Excel
                </Button>
              </div>
            </div>

            {/* Step 2: Fill Template */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold">Completa los Datos</h3>
              </div>
              <div className="ml-10 space-y-2">
                <p className="text-sm text-gray-600">
                  Completa la plantilla con la información de tus clientes:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li><strong>nombre_cliente:</strong> Nombre completo del cliente</li>
                  <li><strong>cedula:</strong> Número de cédula (será la contraseña inicial)</li>
                  <li><strong>telefono:</strong> Número de teléfono del cliente</li>
                  <li><strong>correo:</strong> Email del cliente (será el usuario para login)</li>
                  <li><strong>codigo_casillero:</strong> Código de casillero asignado (debe ser único)</li>
                  <li><strong>tipo_cliente:</strong> "publico" o "alumno"</li>
                  <li><strong>ciudad:</strong> Ciudad del cliente</li>
                  <li><strong>direccion_entrega:</strong> Dirección completa de entrega</li>
                </ul>
              </div>
            </div>

          {/* Step 3: Upload File */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold">Sube el Archivo</h3>
            </div>
            <div className="ml-10 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
              </div>
              
              {importFile && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <FileText size={16} />
                  <span>{importFile.name}</span>
                </div>
              )}

              <Button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="gap-2"
              >
                {importing ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Procesando archivo...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Importar Clientes
                  </>
                )}
              </Button>
              
              {/* Progress indicator during import */}
              {importing && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <div>
                      <p className="font-semibold text-blue-800">Importando clientes...</p>
                      <p className="text-sm text-blue-600">Por favor espera mientras se procesan los datos del archivo Excel.</p>
                    </div>
                  </div>
                  <div className="mt-3 bg-blue-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-blue-600 h-full rounded-full animate-[loading_2s_ease-in-out_infinite]" style={{width: '70%'}}></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Import Results */}
          {importResult && (
            <div className="border-2 border-gray-200 rounded-lg p-4 space-y-4 bg-white shadow-sm">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {importResult.error ? (
                  <XCircle className="text-red-500" size={24} />
                ) : (
                  <CheckCircle className="text-green-500" size={24} />
                )}
                Resultados de la Importación
              </h3>
              
              {/* Error message if import failed */}
              {importResult.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle size={20} />
                    <span className="font-semibold">Error en la importación</span>
                  </div>
                  <p className="text-red-600 mt-2">{importResult.error}</p>
                </div>
              )}
              
              {/* Summary */}
              {!importResult.error && (
                <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle size={20} />
                      <span className="font-semibold">Importados Exitosamente</span>
                    </div>
                  <p className="text-3xl font-bold text-green-600 mt-2">
                    {importResult.imported_count}
                  </p>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle size={20} />
                    <span className="font-semibold">Errores</span>
                  </div>
                  <p className="text-3xl font-bold text-red-600 mt-2">
                    {importResult.error_count}
                  </p>
                </div>
              </div>

              {/* Imported Clients */}
              {importResult.imported_clients && importResult.imported_clients.length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-700 mb-2">Clientes Importados:</h4>
                  <div className="max-h-60 overflow-y-auto border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">Nombre</th>
                          <th className="px-4 py-2 text-left">Cédula</th>
                          <th className="px-4 py-2 text-left">Teléfono</th>
                          <th className="px-4 py-2 text-left">Correo</th>
                          <th className="px-4 py-2 text-left">Casillero</th>
                          <th className="px-4 py-2 text-left">Tipo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.imported_clients.map((client, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2">{client.nombre_cliente}</td>
                            <td className="px-4 py-2">{client.cedula}</td>
                            <td className="px-4 py-2">{client.telefono || '-'}</td>
                            <td className="px-4 py-2">{client.correo}</td>
                            <td className="px-4 py-2 font-mono">{client.codigo_casillero}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                client.tipo_cliente === 'alumno' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {client.tipo_cliente === 'alumno' ? 'Alumno' : 'Público'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {importResult.errors && importResult.errors.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Errores Encontrados:
                  </h4>
                  <div className="max-h-60 overflow-y-auto border rounded bg-red-50">
                    <table className="w-full text-sm">
                      <thead className="bg-red-100 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">Fila</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.errors.map((error, idx) => (
                          <tr key={idx} className="border-t border-red-200">
                            <td className="px-4 py-2">{error.row}</td>
                            <td className="px-4 py-2">{error.email}</td>
                            <td className="px-4 py-2 text-red-700">{error.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          )}
          </CardContent>
        </Card>
      )}

      {/* UPDATE TAB */}
      {activeTab === 'update' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <RefreshCw className="text-green-600" />
              Actualización Masiva de Clientes
            </CardTitle>
            <CardDescription>
              Actualiza tipo de cliente, teléfono, ciudad y dirección de clientes existentes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Download Current Clients */}
            <div className="border-2 border-green-200 rounded-lg p-4 space-y-3 bg-green-50">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold text-green-800">Descarga los Clientes Actuales</h3>
              </div>
              <p className="text-sm text-green-700 ml-10">
                Descarga el archivo Excel con todos los clientes actuales para modificarlos.
              </p>
              <div className="ml-10">
                <Button onClick={handleDownloadClientsForUpdate} variant="default" className="gap-2 bg-green-600 hover:bg-green-700">
                  <Download size={16} />
                  Descargar Clientes para Actualizar
                </Button>
              </div>
            </div>

            {/* Step 2: Modify Data */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold">Modifica los Datos</h3>
              </div>
              <div className="ml-10 space-y-2">
                <p className="text-sm text-gray-600">
                  Campos que puedes actualizar:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li><strong>tipo_cliente:</strong> Cambiar entre "publico" y "alumno"</li>
                  <li><strong>telefono:</strong> Actualizar número de teléfono</li>
                  <li><strong>cedula:</strong> Actualizar cédula</li>
                  <li><strong>ciudad:</strong> Actualizar ciudad</li>
                  <li><strong>direccion_entrega:</strong> Actualizar dirección</li>
                </ul>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Importante:</strong> El campo "correo" se usa para identificar al cliente. NO lo modifiques.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3: Upload Updated File */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold">Sube el Archivo Modificado</h3>
              </div>
              <div className="ml-10 space-y-3">
                <input
                  id="update-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleUpdateFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                />
                {updateFile && (
                  <div className="flex items-center gap-2 text-green-600">
                    <FileText size={16} />
                    <span className="text-sm">{updateFile.name}</span>
                  </div>
                )}
                <Button
                  onClick={handleBulkUpdate}
                  disabled={!updateFile || updating}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  {updating ? (
                    <>
                      <RefreshCw className="animate-spin" size={16} />
                      Actualizando...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Actualizar Clientes
                    </>
                  )}
                </Button>
                
                {/* Progress indicator during update */}
                {updating && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                      <div>
                        <p className="font-semibold text-green-800">Actualizando clientes...</p>
                        <p className="text-sm text-green-600">Por favor espera mientras se procesan los cambios del archivo Excel.</p>
                      </div>
                    </div>
                    <div className="mt-3 bg-green-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-green-600 h-full rounded-full animate-[loading_2s_ease-in-out_infinite]" style={{width: '70%'}}></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Update Results */}
            {updateResult && (
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="text-lg font-semibold">Resultados de la Actualización</h3>
                
                {updateResult.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle size={20} />
                      <span className="font-semibold">Error</span>
                    </div>
                    <p className="text-red-600 mt-2">{updateResult.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle size={20} />
                          <span className="font-semibold">Actualizados</span>
                        </div>
                        <p className="text-3xl font-bold text-green-600 mt-2">
                          {updateResult.updated_count}
                        </p>
                      </div>
                      
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-red-700">
                          <XCircle size={20} />
                          <span className="font-semibold">Errores</span>
                        </div>
                        <p className="text-3xl font-bold text-red-600 mt-2">
                          {updateResult.error_count}
                        </p>
                      </div>
                    </div>

                    {/* Updated Clients */}
                    {updateResult.updated_clients && updateResult.updated_clients.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-green-700 mb-2">Clientes Actualizados:</h4>
                        <div className="max-h-60 overflow-y-auto border rounded">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left">Correo</th>
                                <th className="px-4 py-2 text-left">Nombre</th>
                                <th className="px-4 py-2 text-left">Campos Actualizados</th>
                              </tr>
                            </thead>
                            <tbody>
                              {updateResult.updated_clients.map((client, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="px-4 py-2">{client.correo}</td>
                                  <td className="px-4 py-2">{client.nombre}</td>
                                  <td className="px-4 py-2">
                                    <span className="text-green-600">
                                      {client.campos_actualizados?.join(', ') || '-'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Errors */}
                    {updateResult.errors && updateResult.errors.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                          <AlertCircle size={16} />
                          Errores Encontrados:
                        </h4>
                        <div className="max-h-60 overflow-y-auto border rounded bg-red-50">
                          <table className="w-full text-sm">
                            <thead className="bg-red-100 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left">Fila</th>
                                <th className="px-4 py-2 text-left">Email</th>
                                <th className="px-4 py-2 text-left">Error</th>
                              </tr>
                            </thead>
                            <tbody>
                              {updateResult.errors.map((error, idx) => (
                                <tr key={idx} className="border-t border-red-200">
                                  <td className="px-4 py-2">{error.row}</td>
                                  <td className="px-4 py-2">{error.email}</td>
                                  <td className="px-4 py-2 text-red-700">{error.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Información Importante</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <p>• Los clientes importados podrán iniciar sesión usando su <strong>correo</strong> como usuario.</p>
          <p>• La contraseña inicial será su <strong>número de cédula</strong>.</p>
          <p>• El código de casillero debe ser único y no debe estar ya asignado.</p>
          <p>• Si hay errores en algunas filas, los clientes válidos se importarán de todas formas.</p>
          <p>• Para actualizar clientes existentes, usa la pestaña <strong>"Actualizar Clientes Existentes"</strong>.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientImport;
